import { useState } from 'react'
import { Checkbox, Stack, Text, Card, Group, ActionIcon, Collapse, Badge } from '@mantine/core'
import { IconFilter, IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import { useSightingCategories } from '../../hooks/useSpottings'

interface CategoryFilterProps {
    selectedCategoryIds: string[]
    onCategoryChange: (categoryIds: string[]) => void
    isMobile?: boolean // Add mobile prop for responsive behavior
}

export function CategoryFilter({ selectedCategoryIds, onCategoryChange, isMobile = false }: CategoryFilterProps) {
    const [isExpanded, setIsExpanded] = useState(false)
    const { data: categories, isLoading } = useSightingCategories()

    const handleCategoryToggle = (categoryId: string, checked: boolean) => {
        if (checked) {
            // Add category to selection
            onCategoryChange([...selectedCategoryIds, categoryId])
        } else {
            // Remove category from selection
            onCategoryChange(selectedCategoryIds.filter(id => id !== categoryId))
        }
    }

    const handleSelectAll = (event: React.MouseEvent) => {
        event.stopPropagation() // Prevent event bubbling
        console.log('🔵 Select All clicked')
        if (categories) {
            onCategoryChange(categories.map(cat => cat.id))
        }
    }

    const handleClearAll = (event: React.MouseEvent) => {
        event.stopPropagation() // Prevent event bubbling
        console.log('🔴 Clear All clicked')
        onCategoryChange([])
    }

    if (isLoading || !categories) {
        return null
    }

    const selectedCount = selectedCategoryIds.length
    const totalCount = categories.length
    const isAllSelected = selectedCount === totalCount
    const isNoneSelected = selectedCount === 0

    return (
        <Card
            withBorder={false}
            padding={isMobile ? "xs" : "sm"}
            radius="md"
            style={{
                backgroundColor: 'transparent',
                // Mobile-specific optimizations
                ...(isMobile && {
                    marginBottom: '8px',
                    flexShrink: 0, // Prevent shrinking in mobile layout
                })
            }}
        >
            <Group justify="space-between" align="center" onClick={() => setIsExpanded(!isExpanded)} style={{ cursor: 'pointer' }}>
                <Group gap="xs">
                    <IconFilter size={16} color="var(--mantine-color-blue-6)" />
                    <Text fw={500} size="sm">Category Filter</Text>
                    {!isNoneSelected && (
                        <Badge size="sm" variant="filled" color="blue">
                            {selectedCount}
                        </Badge>
                    )}
                </Group>
                <ActionIcon variant="subtle" size="sm">
                    {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </ActionIcon>
            </Group>

            <Collapse in={isExpanded}>
                <Stack gap="xs" mt="sm">
                    {/* Select All / Clear All Controls */}
                    <Group justify="space-between">
                        <Text
                            size="xs"
                            c="blue"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={handleSelectAll}
                        >
                            Select All
                        </Text>
                        <Text
                            size="xs"
                            c="red"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={handleClearAll}
                        >
                            Clear All
                        </Text>
                    </Group>

                    {/* Category Checkboxes */}
                    <Stack
                        gap="xs"
                        style={{
                            // Mobile-specific scroll handling
                            ...(isMobile && {
                                maxHeight: '200px',
                                overflowY: 'auto',
                            })
                        }}
                    >
                        {categories.map((category) => (
                            <Checkbox
                                key={category.id}
                                label={category.name}
                                checked={selectedCategoryIds.includes(category.id)}
                                onChange={(event) => handleCategoryToggle(category.id, event.currentTarget.checked)}
                                size="sm"
                                styles={{
                                    label: {
                                        fontSize: '14px',
                                        fontWeight: 500,
                                    },
                                }}
                            />
                        ))}
                    </Stack>

                    {/* Summary */}
                    <Text size="xs" c="dimmed" ta="center" mt="xs">
                        {isAllSelected ? 'All categories selected' :
                            isNoneSelected ? 'No categories selected' :
                                `${selectedCount} of ${totalCount} categories selected`}
                    </Text>
                </Stack>
            </Collapse>
        </Card>
    )
} 